import React, { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
interface Sale {
  id: string;
  sale_date: string;
  total_amount: number;
  quantity_sold: number;
  product_id: string;
  product_name: string;
  unit_price: number;
  payment_method: string;
}
interface ComparativeStatsProps {
  sales: Sale[];
  customColor?: string;
}
export const ComparativeStats: React.FC<ComparativeStatsProps> = ({
  sales,
  customColor
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState(1);
  
  const periods = [
    { months: 1, label: '1 mois' },
    { months: 2, label: '2 mois' },
    { months: 3, label: '3 mois' },
    { months: 6, label: '6 mois' },
    { months: 12, label: '1 an' },
    { months: 24, label: '2 ans' },
  ];

  const formatPrice = (price: number) => new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR'
  }).format(price);
  const calculateStats = useMemo(() => {
    const now = new Date();
    
    // Calculer toutes les comparaisons avec la période actuelle
    const comparisons = periods.map(period => {
      const currentPeriodStart = new Date(now.getFullYear(), now.getMonth() - period.months, now.getDate());
      const previousPeriodStart = new Date(now.getFullYear(), now.getMonth() - (period.months * 2), now.getDate());
      
      // Période actuelle 
      const currentPeriodSales = sales.filter(sale => {
        const saleDate = new Date(sale.sale_date);
        return saleDate >= currentPeriodStart && saleDate <= now;
      });

      // Période précédente (même durée)
      const previousPeriodSales = sales.filter(sale => {
        const saleDate = new Date(sale.sale_date);
        return saleDate >= previousPeriodStart && saleDate < currentPeriodStart;
      });

      const calculatePeriodStats = (periodSales: Sale[]) => ({
        totalRevenue: periodSales.reduce((sum, sale) => sum + sale.total_amount, 0),
        totalSales: periodSales.length,
        totalItems: periodSales.reduce((sum, sale) => sum + sale.quantity_sold, 0),
        avgOrderValue: periodSales.length > 0 ? periodSales.reduce((sum, sale) => sum + sale.total_amount, 0) / periodSales.length : 0
      });
      
      const current = calculatePeriodStats(currentPeriodSales);
      const previous = calculatePeriodStats(previousPeriodSales);
      
      const calculateChange = (current: number, previous: number) => {
        if (previous === 0) return current > 0 ? 100 : 0;
        return (current - previous) / previous * 100;
      };
      
      return {
        period: period.label,
        current,
        previous,
        revenueChange: calculateChange(current.totalRevenue, previous.totalRevenue),
        salesChange: calculateChange(current.totalSales, previous.totalSales),
        itemsChange: calculateChange(current.totalItems, previous.totalItems),
        avgOrderChange: calculateChange(current.avgOrderValue, previous.avgOrderValue)
      };
    });
    
    return { comparisons };
  }, [sales, periods]);
  const getChangeIcon = (change: number) => {
    if (change > 0) return <TrendingUp className="w-4 h-4 text-green-600" />;
    if (change < 0) return <TrendingDown className="w-4 h-4 text-red-600" />;
    return <Minus className="w-4 h-4 text-muted-foreground" />;
  };
  const getChangeColor = (change: number) => {
    if (change > 0) return 'text-green-600';
    if (change < 0) return 'text-red-600';
    return 'text-muted-foreground';
  };
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-foreground">Statistiques comparatives</h3>
      
      {/* Grille avec scroll horizontal - 2 par ligne */}
      <div className="overflow-x-auto">
        <div className="grid grid-cols-2 gap-4 min-w-max w-full pb-2" style={{ minWidth: '800px' }}>
          {calculateStats.comparisons.map((comparison) => (
            <Card key={comparison.period} className="p-4 min-w-[380px]">
              <CardContent className="p-0">
                <h4 className="font-medium text-muted-foreground mb-3">
                  {comparison.period} actuels vs précédents
                </h4>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Chiffre d'affaires</span>
                    <div className="flex items-center gap-2">
                      {getChangeIcon(comparison.revenueChange)}
                      <span className={cn("text-sm font-medium", getChangeColor(comparison.revenueChange))}>
                        {comparison.revenueChange > 0 ? '+' : ''}{comparison.revenueChange.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Actuel: {formatPrice(comparison.current.totalRevenue)} | Précédent: {formatPrice(comparison.previous.totalRevenue)}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Nombre de ventes</span>
                    <div className="flex items-center gap-2">
                      {getChangeIcon(comparison.salesChange)}
                      <span className={cn("text-sm font-medium", getChangeColor(comparison.salesChange))}>
                        {comparison.salesChange > 0 ? '+' : ''}{comparison.salesChange.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Actuel: {comparison.current.totalSales} | Précédent: {comparison.previous.totalSales}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Articles vendus</span>
                    <div className="flex items-center gap-2">
                      {getChangeIcon(comparison.itemsChange)}
                      <span className={cn("text-sm font-medium", getChangeColor(comparison.itemsChange))}>
                        {comparison.itemsChange > 0 ? '+' : ''}{comparison.itemsChange.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Actuel: {comparison.current.totalItems} | Précédent: {comparison.previous.totalItems}
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Panier moyen</span>
                    <div className="flex items-center gap-2">
                      {getChangeIcon(comparison.avgOrderChange)}
                      <span className={cn("text-sm font-medium", getChangeColor(comparison.avgOrderChange))}>
                        {comparison.avgOrderChange > 0 ? '+' : ''}{comparison.avgOrderChange.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Actuel: {formatPrice(comparison.current.avgOrderValue)} | Précédent: {formatPrice(comparison.previous.avgOrderValue)}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};